import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Users, BookOpen, DollarSign, Mail, FileText, 
  TrendingUp, MessageSquare, Send, Settings, Loader2,
  BarChart3, Shield, Tag, GraduationCap, CalendarCheck, UserPlus, Clock, Zap, Trophy
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [digestPreview, setDigestPreview] = useState(null);
  const [digestLoading, setDigestLoading] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [triggeringFollowup, setTriggeringFollowup] = useState(false);
  const [allCourses, setAllCourses] = useState([]);
  const [courseOfWeekId, setCourseOfWeekId] = useState('');
  const [savingCOW, setSavingCOW] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchDigestPreview();
    fetchSchedulerStatus();
    fetchCourseOfWeek();
  }, []);

  const fetchCourseOfWeek = async () => {
    try {
      const [cowRes, coursesRes, academyRes] = await Promise.all([
        axios.get(`${API}/course-of-week`),
        axios.get(`${API}/courses`),
        axios.get(`${API}/academy/courses`).catch(() => ({ data: [] }))
      ]);
      const regularCourses = (coursesRes.data || []).map(c => ({ ...c, _source: 'course' }));
      const academyCourses = (academyRes.data || []).map(c => ({ ...c, _source: 'academy' }));
      setAllCourses([...regularCourses, ...academyCourses]);
      setCourseOfWeekId(cowRes.data.course?.id || '');
    } catch (error) {
      console.error('Failed to fetch course of week:', error);
    }
  };

  const saveCourseOfWeek = async (courseId) => {
    setSavingCOW(true);
    try {
      await axios.put(`${API}/admin/course-of-week`, { course_id: courseId || null }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourseOfWeekId(courseId);
      toast.success(courseId ? 'Course of the Week updated!' : 'Course of the Week cleared');
    } catch (error) {
      toast.error('Failed to update');
    } finally { setSavingCOW(false); }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDigestPreview = async () => {
    try {
      const response = await axios.get(`${API}/admin/digest/preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDigestPreview(response.data);
    } catch (error) {
      console.error('Failed to fetch digest preview:', error);
    }
  };

  const sendDigest = async () => {
    if (!window.confirm(`Send weekly digest to ${digestPreview?.recipients || 0} recipients (${digestPreview?.breakdown?.members || 0} members, ${digestPreview?.breakdown?.subscribers || 0} subscribers, ${digestPreview?.breakdown?.leads || 0} leads)?`)) return;
    setSendingDigest(true);
    try {
      const response = await axios.post(`${API}/admin/digest/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Digest sent to ${response.data.sent} members!`);
      fetchDigestPreview();
      fetchSchedulerStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send digest');
    } finally {
      setSendingDigest(false);
    }
  };

  const fetchSchedulerStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/scheduler/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchedulerStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch scheduler status:', error);
    }
  };

  const triggerFollowup = async () => {
    setTriggeringFollowup(true);
    try {
      const response = await axios.post(`${API}/admin/leads/followup/trigger`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      if (data.newly_sent > 0) {
        toast.success(`Follow-up sent to ${data.newly_sent} new leads!`);
      } else {
        toast.info(`No new leads to follow up. ${data.already_followed_up} already contacted, ${data.total_unconverted_leads} total unconverted.`);
      }
      fetchSchedulerStatus();
    } catch (error) {
      toast.error('Failed to trigger follow-up');
    } finally {
      setTriggeringFollowup(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-24 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Users',
      value: stats?.users?.total || 0,
      subtitle: `${stats?.users?.students || 0} students, ${stats?.users?.instructors || 0} instructors`,
      icon: Users,
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Courses',
      value: stats?.courses?.total || 0,
      subtitle: `${stats?.courses?.published || 0} published`,
      icon: BookOpen,
      color: 'bg-purple-100 text-purple-600'
    },
    {
      title: 'Total Revenue',
      value: `$${stats?.revenue?.total?.toFixed(2) || '0.00'}`,
      subtitle: `Platform earned $${stats?.revenue?.platform_earnings?.toFixed(2) || '0.00'}`,
      icon: DollarSign,
      color: 'bg-green-100 text-green-600'
    },
    {
      title: 'Purchases',
      value: stats?.revenue?.total_purchases || 0,
      subtitle: 'Total course purchases',
      icon: TrendingUp,
      color: 'bg-amber-100 text-amber-600'
    }
  ];

  const quickActions = [
    {
      title: 'Create Course',
      description: 'Add a new platform course',
      icon: BookOpen,
      path: '/admin/courses/create',
      color: 'text-primary'
    },
    {
      title: 'Blog Manager',
      description: 'Create and manage blog posts',
      icon: FileText,
      path: '/admin/blog',
      color: 'text-purple-600'
    },
    {
      title: 'Newsletter',
      description: `${stats?.engagement?.newsletter_subscribers || 0} subscribers`,
      icon: Send,
      path: '/admin/newsletter',
      color: 'text-blue-600'
    },
    {
      title: 'Messages',
      description: `${stats?.engagement?.unread_messages || 0} unread`,
      icon: MessageSquare,
      path: '/admin/messages',
      badge: stats?.engagement?.unread_messages > 0 ? stats.engagement.unread_messages : null,
      color: 'text-green-600'
    },
    {
      title: 'Course Moderation',
      description: `${stats?.courses?.total || 0} total courses`,
      icon: Settings,
      path: '/admin/courses',
      color: 'text-amber-600'
    },
    {
      title: 'Coupons',
      description: 'Create discount codes',
      icon: Tag,
      path: '/admin/coupons',
      color: 'text-pink-600'
    },
    {
      title: 'User Management',
      description: 'View and manage users',
      icon: Users,
      path: '/admin/users',
      color: 'text-red-600'
    },
    {
      title: 'AI Academy',
      description: 'Manage academy courses & members',
      icon: GraduationCap,
      path: '/academy/admin',
      color: 'text-indigo-600'
    },
    {
      title: 'Leads',
      description: 'Preview leads & email campaigns',
      icon: UserPlus,
      path: '/admin/leads',
      color: 'text-teal-600'
    },
    {
      title: 'Unconverted Users',
      description: 'Registered users without a plan',
      icon: Users,
      path: '/admin/unconverted',
      color: 'text-amber-600'
    },
    {
      title: 'Student Enrollments',
      description: 'View student course enrollments',
      icon: BookOpen,
      path: '/admin/enrollments',
      color: 'text-cyan-600'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <Navbar />

      <div className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-slate-900">
                Admin Dashboard
              </h1>
              <p className="text-slate-600">Platform management and analytics</p>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {statCards.map((stat, index) => (
              <Card key={index} className="border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.color}`}>
                      <stat.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                      <p className="text-sm text-slate-600">{stat.title}</p>
                      <p className="text-xs text-slate-400">{stat.subtitle}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Quick Actions */}
          <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">Quick Actions</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {quickActions.map((action, index) => (
              <Card 
                key={index} 
                className="border-slate-200 hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => navigate(action.path)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center ${action.color}`}>
                      <action.icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-slate-900">{action.title}</h3>
                        {action.badge && (
                          <Badge className="bg-red-500">{action.badge}</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500">{action.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Course of the Week */}
          <Card className="border-amber-200 bg-amber-50/50 mb-8" data-testid="course-of-week-admin">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Trophy className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Course of the Week</h3>
                    <p className="text-xs text-slate-500">Featured prominently on the landing page</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-1 sm:max-w-sm">
                  <Select value={courseOfWeekId} onValueChange={(val) => saveCourseOfWeek(val === 'none' ? '' : val)}>
                    <SelectTrigger className="bg-white" data-testid="cow-select">
                      <SelectValue placeholder="Select a course..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- None (clear) --</SelectItem>
                      {allCourses.filter(c => c._source === 'course').length > 0 && (
                        <div className="px-2 py-1 text-xs font-semibold text-slate-400 uppercase">Courses</div>
                      )}
                      {allCourses.filter(c => c._source === 'course').map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                      ))}
                      {allCourses.filter(c => c._source === 'academy').length > 0 && (
                        <div className="px-2 py-1 text-xs font-semibold text-slate-400 uppercase mt-1">AI Academy</div>
                      )}
                      {allCourses.filter(c => c._source === 'academy').map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {savingCOW && <Loader2 className="w-4 h-4 animate-spin text-amber-600" />}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Engagement Stats */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                Engagement Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-4 gap-6">
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-3xl font-bold text-slate-900">{stats?.engagement?.newsletter_subscribers || 0}</p>
                  <p className="text-sm text-slate-600">Newsletter Subscribers</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-3xl font-bold text-slate-900">{stats?.engagement?.unread_messages || 0}</p>
                  <p className="text-sm text-slate-600">Unread Messages</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-3xl font-bold text-slate-900">{stats?.engagement?.blog_posts || 0}</p>
                  <p className="text-sm text-slate-600">Blog Posts</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-3xl font-bold text-slate-900">{stats?.engagement?.published_blog_posts || 0}</p>
                  <p className="text-sm text-slate-600">Published Posts</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Weekly Digest */}
          <Card className="border-slate-200 mt-6" data-testid="weekly-digest-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <CalendarCheck className="w-5 h-5 text-indigo-600" />
                Weekly Learning Digest
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row md:items-center gap-6">
                <div className="flex-1">
                  <p className="text-sm text-slate-600 mb-3">
                    Send weekly updates to all subscribers, registered users, leads, and active members. Members get personalized stats; others get course highlights.
                  </p>
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <div className="flex items-center gap-1.5">
                      <Users className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-700 font-medium">{digestPreview?.recipients || 0}</span>
                      <span className="text-slate-500">total recipients</span>
                    </div>
                    {digestPreview?.breakdown && (
                      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
                        <span>{digestPreview.breakdown.members} members</span>
                        <span>|</span>
                        <span>{digestPreview.breakdown.registered_users} users</span>
                        <span>|</span>
                        <span>{digestPreview.breakdown.subscribers} subscribers</span>
                        <span>|</span>
                        <span>{digestPreview.breakdown.leads} leads</span>
                      </div>
                    )}
                  </div>
                  {digestPreview?.last_sent && (
                    <div className="flex items-center gap-1.5 mt-2 text-sm">
                      <CalendarCheck className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-500">Last sent: {new Date(digestPreview.last_sent).toLocaleDateString()}</span>
                      <span className="text-slate-400">({digestPreview.last_sent_count} sent)</span>
                    </div>
                  )}
                  {digestPreview?.preview && (
                    <div className="mt-3 flex items-center gap-3 text-xs text-slate-400">
                      <span>Your preview: {digestPreview.preview.lessons_completed} lessons, {digestPreview.preview.current_streak} streak, {digestPreview.preview.avg_score}% avg score</span>
                    </div>
                  )}
                </div>
                <Button
                  onClick={sendDigest}
                  disabled={sendingDigest || !digestPreview?.recipients}
                  className="bg-indigo-600 hover:bg-indigo-700 shrink-0"
                  data-testid="send-digest-btn"
                >
                  {sendingDigest ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Weekly Digest
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Automation Status */}
          <Card className="border-slate-200 mt-6" data-testid="automation-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                Automations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <h4 className="font-medium text-slate-900 text-sm">Weekly Digest</h4>
                    <Badge variant="outline" className="text-xs ml-auto">Auto</Badge>
                  </div>
                  <p className="text-xs text-slate-500 mb-2">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {schedulerStatus?.digest?.schedule || 'Every Monday 9:00 AM UTC'}
                  </p>
                  {schedulerStatus?.digest?.last_sent && (
                    <p className="text-xs text-slate-400">
                      Last: {new Date(schedulerStatus.digest.last_sent).toLocaleDateString()} ({schedulerStatus.digest.last_count} sent)
                    </p>
                  )}
                </div>
                <div className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <h4 className="font-medium text-slate-900 text-sm">Lead Follow-up</h4>
                    <Badge variant="outline" className="text-xs ml-auto">Auto</Badge>
                  </div>
                  <p className="text-xs text-slate-500 mb-2">
                    <Clock className="w-3 h-3 inline mr-1" />
                    Daily at 10:00 AM UTC (3-day unconverted leads)
                  </p>
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <span>{schedulerStatus?.lead_followup?.total_unconverted || 0} unconverted leads</span>
                      <span>|</span>
                      <span>{schedulerStatus?.lead_followup?.pending_followups || 0} pending</span>
                      <span>|</span>
                      <span>{schedulerStatus?.lead_followup?.already_followed_up || 0} sent</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={triggerFollowup}
                      disabled={triggeringFollowup}
                      className="text-xs h-7 mt-1 w-fit"
                      data-testid="trigger-followup-btn"
                    >
                      {triggeringFollowup ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Send Now'}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
